'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Calculator, Ruler, Download, Share2, AlertCircle, CheckCircle, Info, ArrowRight } from 'lucide-react';
import RelatedLinks from '@/components/seo/RelatedLinks';

interface Calculation {
  mountType: 'inside' | 'outside';
  windowWidth: number;
  windowHeight: number;
  windowDepth?: number;
  recommendedWidth: number;
  recommendedHeight: number;
  notes: string[];
}

type MountType = 'inside' | 'outside';

export default function SizeCalculatorPage() {
  const [mountType, setMountType] = useState<MountType>('inside');
  const [windowWidth, setWindowWidth] = useState<string>('');
  const [windowHeight, setWindowHeight] = useState<string>('');
  const [windowDepth, setWindowDepth] = useState<string>('');
  const [calculation, setCalculation] = useState<Calculation | null>(null);
  const [showResults, setShowResults] = useState(false);

  const calculateSize = () => {
    const width = parseFloat(windowWidth);
    const height = parseFloat(windowHeight);
    const depth = parseFloat(windowDepth) || 0;

    if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
      return;
    }

    let recommendedWidth = width;
    let recommendedHeight = height;
    const notes: string[] = [];

    if (mountType === 'inside') {
      // Inside mount: Deduct 1/4" for operating clearance
      recommendedWidth = width - 0.25;
      recommendedHeight = height - 0.25;

      notes.push('Deducted 1/4" from width and height for operating clearance');

      if (depth < 1.5) {
        notes.push('⚠️ Window depth less than 1.5" - May not accommodate all blind types');
      }

      if (depth >= 1.5 && depth < 2.5) {
        notes.push('✓ Suitable for most roller shades and cellular shades');
        notes.push('⚠️ May be tight for some wood blinds - check product specifications');
      }

      if (depth >= 2.5) {
        notes.push('✓ Excellent depth - Accommodates all blind types including wood blinds');
      }
    } else {
      // Outside mount: Add 3" to each side
      recommendedWidth = width + 6;
      recommendedHeight = height + 4;

      notes.push('Added 3" to each side of width for light blockage');
      notes.push('Added 2" to top and bottom of height for better coverage');
      notes.push('Ensure adequate wall/trim space for mounting brackets');
    }

    // Round to nearest 1/8"
    recommendedWidth = Math.round(recommendedWidth * 8) / 8;
    recommendedHeight = Math.round(recommendedHeight * 8) / 8;

    setCalculation({
      mountType,
      windowWidth: width,
      windowHeight: height,
      windowDepth: depth,
      recommendedWidth,
      recommendedHeight,
      notes,
    });

    setShowResults(true);
  };

  const formatDimension = (value: number): string => {
    const inches = Math.floor(value);
    const fraction = value - inches;

    if (fraction === 0) return `${inches}"`;

    // Convert to fraction
    if (fraction === 0.125) return `${inches} 1/8"`;
    if (fraction === 0.25) return `${inches} 1/4"`;
    if (fraction === 0.375) return `${inches} 3/8"`;
    if (fraction === 0.5) return `${inches} 1/2"`;
    if (fraction === 0.625) return `${inches} 5/8"`;
    if (fraction === 0.75) return `${inches} 3/4"`;
    if (fraction === 0.875) return `${inches} 7/8"`;

    return `${value.toFixed(2)}"`;
  };

  const downloadResults = () => {
    if (!calculation) return;

    const content = `
BLIND SIZE CALCULATION RESULTS
==============================

Mount Type: ${calculation.mountType === 'inside' ? 'Inside Mount' : 'Outside Mount'}

YOUR MEASUREMENTS:
- Window Width: ${calculation.windowWidth}"
- Window Height: ${calculation.windowHeight}"
${calculation.windowDepth ? `- Window Depth: ${calculation.windowDepth}"` : ''}

RECOMMENDED ORDER SIZE:
- Width: ${formatDimension(calculation.recommendedWidth)}
- Height: ${formatDimension(calculation.recommendedHeight)}

NOTES:
${calculation.notes.map((note) => `- ${note}`).join('\n')}

Generated by SmartBlinds Size Calculator
${new Date().toLocaleDateString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blind-size-calculation.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const shareResults = async () => {
    if (!calculation) return;

    const text = `My custom blind size: ${formatDimension(calculation.recommendedWidth)} W x ${formatDimension(calculation.recommendedHeight)} H (${calculation.mountType} mount)`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Blind Size Calculation',
          text: text,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled share
      }
    } else {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    }
  };

  const resetCalculator = () => {
    setWindowWidth('');
    setWindowHeight('');
    setWindowDepth('');
    setCalculation(null);
    setShowResults(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-primary-red text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
              <Calculator className="w-4 h-4" />
              <span className="text-sm font-semibold">Free Tool</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Blind Size Calculator</h1>
            <p className="text-xl text-white/90 mb-8">
              Calculate the perfect size for your custom blinds and shades in seconds. Get instant recommendations
              for inside or outside mount installations.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Input Form */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Enter Your Measurements</h2>

                {/* Mount Type Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Mount Type *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setMountType('inside')}
                      className={`p-4 rounded-lg border-2 transition-all ${ mountType === 'inside' ? 'border-primary-red /5 text-primary-red' : 'border-gray-200 hover:border-gray-300' }`}
                    >
                      <div className="font-semibold mb-1">Inside Mount</div>
                      <div className="text-xs text-gray-600">Fits inside window frame</div>
                    </button>
                    <button
                      onClick={() => setMountType('outside')}
                      className={`p-4 rounded-lg border-2 transition-all ${ mountType === 'outside' ? 'border-primary-red /5 text-primary-red' : 'border-gray-200 hover:border-gray-300' }`}
                    >
                      <div className="font-semibold mb-1">Outside Mount</div>
                      <div className="text-xs text-gray-600">Covers entire window</div>
                    </button>
                  </div>
                </div>

                {/* Window Width */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Window Width (inches) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.125"
                      min="0"
                      value={windowWidth}
                      onChange={(e) => setWindowWidth(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-red focus:border-transparent"
                      placeholder="e.g., 36"
                    />
                    <Ruler className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Window Height */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Window Height (inches) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.125"
                      min="0"
                      value={windowHeight}
                      onChange={(e) => setWindowHeight(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-red focus:border-transparent"
                      placeholder="e.g., 48"
                    />
                    <Ruler className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Window Depth (Inside Mount Only) */}
                {mountType === 'inside' && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Window Depth (inches)
                      <span className="text-gray-500 font-normal ml-1">(Optional)</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.125"
                        min="0"
                        value={windowDepth}
                        onChange={(e) => setWindowDepth(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-red focus:border-transparent"
                        placeholder="e.g., 2.5"
                      />
                      <Ruler className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Helps determine compatible blind types
                    </p>
                  </div>
                )}

                {/* Calculate Button */}
                <button
                  onClick={calculateSize}
                  disabled={!windowWidth || !windowHeight}
                  className="w-full bg-primary-red text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-red-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Calculator className="w-5 h-5" />
                  Calculate Size
                </button>

                {showResults && (
                  <button
                    onClick={resetCalculator}
                    className="w-full mt-3 bg-gray-100 text-gray-700 font-medium py-2 px-6 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Reset Calculator
                  </button>
                )}
              </div>

              {/* Results */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                {showResults && calculation ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900">Your Results</h2>
                      <div className="flex gap-2">
                        <button
                          onClick={shareResults}
                          className="p-2 text-gray-600 hover:text-primary-red transition-colors"
                          title="Share"
                        >
                          <Share2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={downloadResults}
                          className="p-2 text-gray-600 hover:text-primary-red transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Recommended Size */}
                    <div className="bg-primary-red/5 border-2 border-primary-red rounded-lg p-6 mb-6">
                      <div className="flex items-center gap-2 text-primary-red mb-3">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">Recommended Order Size</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Width</div>
                          <div className="text-3xl font-bold text-gray-900">
                            {formatDimension(calculation.recommendedWidth)}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 mb-1">Height</div>
                          <div className="text-3xl font-bold text-gray-900">
                            {formatDimension(calculation.recommendedHeight)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mount Type */}
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Mount Type</div>
                      <div className="text-lg text-gray-900">
                        {calculation.mountType === 'inside' ? 'Inside Mount' : 'Outside Mount'}
                      </div>
                    </div>

                    {/* Notes */}
                    {calculation.notes.length > 0 && (
                      <div className="mb-6">
                        <div className="flex items-center gap-2 text-gray-700 font-semibold mb-3">
                          <Info className="w-5 h-5" />
                          <span>Important Notes</span>
                        </div>
                        <ul className="space-y-2">
                          {calculation.notes.map((note, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                              <span className="text-primary-red mt-0.5">•</span>
                              <span>{note}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* CTA */}
                    <div className="pt-6 border-t border-gray-200">
                      <Link
                        href={`/products?width=${calculation.recommendedWidth}&height=${calculation.recommendedHeight}`}
                        className="w-full bg-primary-red text-white font-semibold py-3 px-6 rounded-lg hover:bg-primary-red-dark transition-colors flex items-center justify-center gap-2"
                      >
                        Shop Products for This Size
                        <ArrowRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8">
                    <div className="bg-gray-100 rounded-full p-6 mb-4">
                      <Calculator className="w-16 h-16 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Enter Your Measurements
                    </h3>
                    <p className="text-gray-600">
                      Fill in your window dimensions to get instant size recommendations
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Measure Guide */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              How to Measure Your Windows
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Inside Mount */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Inside Mount</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      1
                    </span>
                    <span>Measure the width at the top, middle, and bottom of the window opening</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      2
                    </span>
                    <span>Use the narrowest width measurement</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      3
                    </span>
                    <span>Measure the height on the left, center, and right</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      4
                    </span>
                    <span>Use the longest height measurement</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      5
                    </span>
                    <span>Measure the depth from the front of the opening to the glass</span>
                  </li>
                </ol>
              </div>

              {/* Outside Mount */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Outside Mount</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      1
                    </span>
                    <span>Measure the exact width of the window opening</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      2
                    </span>
                    <span>Measure the exact height of the window opening</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      3
                    </span>
                    <span>The calculator will add appropriate overlap for light blockage</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-primary-red text-white rounded-full flex items-center justify-center text-sm font-bold">
                      4
                    </span>
                    <span>Ensure adequate wall/trim space around window for brackets</span>
                  </li>
                </ol>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-primary-red flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pro Measuring Tips</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Always use a metal tape measure for accuracy</li>
                    <li>• Measure to the nearest 1/8 inch</li>
                    <li>• Don't assume windows are square - always measure all sides</li>
                    <li>• For inside mount, check that the window frame is deep enough</li>
                    <li>• Write down all measurements immediately to avoid mistakes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  Should I choose inside or outside mount?
                </summary>
                <p className="mt-3 text-gray-700">
                  Inside mount provides a cleaner, more integrated look and is ideal if you have deep enough window
                  frames. Outside mount offers better light blockage and is necessary for shallow window frames or if
                  you want to cover trim/molding.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  What if my window depth is less than 1.5 inches?
                </summary>
                <p className="mt-3 text-gray-700">
                  For shallow windows, we recommend outside mount installation or selecting low-profile products like
                  roller shades. Contact our team for specific product recommendations for your window depth.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  Why do you deduct 1/4" for inside mount?
                </summary>
                <p className="mt-3 text-gray-700">
                  The 1/4" deduction provides necessary operating clearance so your blinds can move smoothly without
                  rubbing against the window frame. This is a standard industry practice for proper functionality.
                </p>
              </details>

              <details className="bg-white rounded-lg p-6 shadow-sm">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  Can I order blinds larger than the calculated size?
                </summary>
                <p className="mt-3 text-gray-700">
                  Our calculator provides optimal sizing based on industry standards. If you need a different size,
                  you can adjust when ordering, but we recommend consulting with our team to ensure proper fit and
                  operation.
                </p>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-4">
          <RelatedLinks
            title="Helpful Resources"
            variant="cards"
            columns={3}
            links={[
              {
                title: 'Measuring Guide',
                href: '/guides/measuring',
                description: 'Detailed guide with photos and videos',
                category: 'Guide',
              },
              {
                title: 'Installation Guide',
                href: '/guides/installation',
                description: 'Step-by-step installation instructions',
                category: 'Guide',
              },
              {
                title: 'Shop All Products',
                href: '/products',
                description: 'Browse our complete selection',
                category: 'Shop',
              },
              {
                title: 'Design Online',
                href: '/samples',
                description: 'Order material samples before you buy',
                category: 'Service',
              },
              {
                title: 'Customer Reviews',
                href: '/reviews',
                description: 'See what customers say about our products',
                category: 'Reviews',
              },
              {
                title: 'Free Consultation',
                href: '/consultation',
                description: 'Get expert help with your project',
                category: 'Service',
              },
            ]}
          />
        </div>
      </section>
    </div>
  );
}
